(ns frontend.view.entity-viewers.activity.history-activity
  (:require
   [cljs.reader :as edn]
   [common.date-formats :as date-formats]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [frontend.analytics :as analytics]
   [frontend.components.inputs.footprint :refer [display-string viewer]]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.activity.events :as events]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.review.view :as review-modal]
   [reagent.core :as r]
   [ui.components.radix-ui.popover.view :as popover]))

(defn header
  [{:keys [:attribute :sort-by? :set-sort-by! :sort-direction]}]
  [:th.history-header-cell
   {:on-click (fn [_]
                (set-sort-by! attribute
                              (if (and sort-by?
                                       (= sort-direction "ASC"))
                                "DESC"
                                "ASC")))}
   (let [fs (registry/get-field-schema @session/tenant-schema attribute)]
     (loc/field-name attribute fs))])

(defn column-hiccup
  [hiccup]
  [:td.history-content-cell
   hiccup])

(defn txt-view
  [txt]
  [:span.overflow-hidden txt])

(defn column-text
  [txt]
  [:td.history-content-cell
   [txt-view txt]])

(defn update-source-label
  [update-source]
  (case update-source
    :api-role/mcp (loc/tr :audit-source/mcp)
    :api-role/outlook-plugin (loc/tr :audit-source/outlook-plugin)
    :api-role/scheduler (loc/tr :audit-source/scheduler)
    (:role/admin :role/user :role/guest-buy-side :role/guest-sell-side
                 :role/guest-lp-portal :role/guest-survey
                 :role/guest-counterparty-diligence)
    (loc/tr :audit-source/browser)
    nil))

(defn actor-with-source
  [actor-name update-source]
  (if-let [source-label (update-source-label update-source)]
    (loc/tr :audit-source/via [actor-name source-label])
    actor-name))

(defn ratings-view
  [timestamp val]
  (let [ratings (edn/read-string (str val))]
    [:ul.overflow-hidden
     (doall (map-indexed (fn [idx [account rated]]
                           ^{:key (str timestamp "-activity-" idx)}
                           [:li
                            (str (entity/display-string (get @session/entities account))
                                 " : "
                                 (int (* 5 rated)))])
                         ratings))]))

(defn datetime-view
  [val]
  (fu/format-date-ts val))

(defn date-view
  [val]
  (fu/format-date-ts val))

(defn status-view
  [_]
  (let [toggled?* (r/atom false)]
    (fn [{:keys [:status-text :pass-reason :on-hold-until]}]
      [:div
       [popover/view
        {:open? (:open? @toggled?*)
         :on-open-change #(swap! toggled?* assoc :open? (not (:open? @toggled?*)))}
        status-text
        [:div

         [:div
          {:class "p-2 flex items-center justify-between font-medium"}
          [:span (loc/tr :details)]
          [:i.fal.fa-times
           {:on-click (fn [_]
                        (swap! toggled?* assoc :open? false))}]]
         [:ul.overflow-hidden
          [:li
           {:class "px-2 py-1 mb-0 text-sm flex gap-3 items-center justify-betwee"}
           [:label (loc/tr :property)]
           [:span (loc/tr :status)]]
          (when pass-reason
            [:li
             [:label (loc/tr :pass-reason)]
             [:span pass-reason]])
          (when on-hold-until
            [:li
             {:class "px-2 py-1 mb-0 text-sm flex gap-3 items-center justify-betwee"}
             [:label (loc/tr :on-hold-until)]
             [:span on-hold-until]])]]]])))

(defn get-display-string
  [value field-schema editor-type new-value]
  (or (when (string? value) value)
      (display-string
       (merge field-schema
              {:editor/type editor-type
               :styling     :plain
               :value       new-value}
              (when (some #{editor-type} [:editor.type/select :editor.type/multi-select])
                {:options (or (:options field-schema)
                              [{:key   new-value
                                :label (some-> @session/entities
                                               (get new-value)
                                               (entity/display-string))}])})))
      "-"))

(defn get-value-hiccup
  [{:keys [update-attribute new-value str-value info timestamp value entity-schema field-schema]}]
  (let [data-type (some->> (get entity-schema update-attribute)
                           :field/data-type)]
    (cond
      (and (= update-attribute :oppo/status)
           (some #{new-value} [:oppo.status/on-hold :oppo.status/passed]))
      [status-view
       (merge {:status-text (str str-value)}
              (when (= new-value :oppo.status/passed)
                {:pass-reason
                 (let [pass-reason (or (:oppo/pass-reason-v2 info)
                                       (:oppo/pass-reason info))
                       pass-reason-string (if (string? pass-reason)
                                            pass-reason
                                            (->> pass-reason
                                                 (get @session/entities)
                                                 entity/display-string))]
                   pass-reason-string)})
              (when (= new-value :oppo.status/on-hold)
                {:on-hold-until (fu/display-date-timestamp (:oppo/on-hold-until info))}))]

      (some #{update-attribute}
            [:oppo/ratings :fundraising-opportunity/ratings :sales-opportunity/ratings])
      [ratings-view timestamp str-value]

      (= data-type :data.type/datetime)
      [datetime-view value]

      (= data-type :data.type/date)
      [date-view value]

      (some #{(->> update-attribute
                   (get entity-schema)
                   :field/data-type)}
            [:data.type/financials :data.type/tesi-financials])
      [viewer
       (merge field-schema
              {:editor/type (if (= :data.type/tesi-financials
                                   (->> update-attribute
                                        (get entity-schema)
                                        :field/data-type))
                              :editor.type/tesi-financials
                              :editor.type/financials)
               :styling     :plain
               :value       (cond (map? value)
                                  value
                                  (string? value)
                                  (edn/read-string value))})]
      :else
      [txt-view
       (str (or (when (and (string? str-value)
                           (empty? str-value))
                  "-")
                str-value))])))

(defn show-details
  [update-attribute entity-schema actor-display-name timestamp date-format timezone value-col-hiccup]
  (when-not (or (some #{update-attribute} [:oppo/ratings :oppo/status])
                (= :data.type/datetime
                   (->> (get entity-schema update-attribute)
                        :field/data-type)))
    (modals/open-modal!
     review-modal/modal-key
     {:title-text  (or (when-let [fs (registry/get-field-schema @session/tenant-schema
                                                                update-attribute)]
                         (loc/field-name update-attribute fs))
                       (loc/tr update-attribute))
      :footer-text (str (loc/tr :modified-by)
                        " "
                        actor-display-name
                        " - "
                        (date-formats/format-millis
                         timestamp
                         date-format
                         timezone))
      :button-text (loc/tr :ok)
      :value       value-col-hiccup
      :on-submit!  identity})))

(defn view
  [entity-id state local-entities entity-type]
  (let [tenant-schema @session/tenant-schema
        sort-state (r/atom {:sort-by-key    :audit/ts
                            :sort-direction "DESC"})
        sort-by! (fn [attr dir]
                   (swap! sort-state assoc
                          :sort-by-key attr
                          :sort-direction dir))
        entity (or (get @local-entities entity-id)
                   (get @session/entities entity-id))
        entity-schema (or (when entity-type
                            (registry/get-schema @session/tenant-schema entity-type))
                          (some->> entity
                                   :entity/type
                                   (registry/get-schema @session/tenant-schema)))
        date-format (str (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                         " HH:mm:ss")
        timezone (or (:tenant/timezone @session/tenant) timezones/default-timezone)]
    (analytics/event "viewed-deal-history")
    (events/open-audit-subscription! {:primary-entity-id entity-id
                                      :state             state})
    (r/create-class
     {:component-will-unmount
      (fn [_]
        (events/stop-audit-subscription! state))
      :reagent-render
      (fn [_entity-id state _local-entities]
        [:div
         (if-not (:timeline-loaded @state)
           [:div.entity-timeline-loading
            [:i.fal.fa-spinner.fa-pulse.fa-2x]
            [:span (loc/tr :updating-activity)]]

           (let [rows (or (:audit-timeline-rows @state) [])
                 pipeline-id (fu/pipeline-id entity)
                 sorted-updates (->> rows
                                     (filterv :add-operation?)
                                     (cu/remove-by :attribute :oppo/reactivated-by)
                                     (filterv (fn [{:keys [attribute]}]
                                                (f-permissions/field-read-access?
                                                 attribute
                                                 entity-type
                                                 pipeline-id)))
                                     (sort-by :timestamp)
                                     (reverse))]
             [:div.history-activity
              [:table.zapflow-table.table.table-striped.table-hover
               [:thead
                [:tr.history-header-row
                 [header
                  {:attribute      :history/property
                   :sort-by?       (= :history/property (:sort-by-key @sort-state))
                   :set-sort-by!   sort-by!
                   :sort-direction (:sort-direction @sort-state)}]
                 [header
                  {:attribute      :history/new-value
                   :sort-by?       (= :history/new-value (:sort-by-key @sort-state))
                   :set-sort-by!   sort-by!
                   :sort-direction (:sort-direction @sort-state)}]
                 [header
                  {:attribute      :history/actor
                   :sort-by?       (= :history/actor (:sort-by-key @sort-state))
                   :set-sort-by!   sort-by!
                   :sort-direction (:sort-direction @sort-state)}]
                 [header
                  {:attribute      :history/timestamp
                   :sort-by?       (= :history/timestamp (:sort-by-key @sort-state))
                   :set-sort-by!   sort-by!
                   :sort-direction (:sort-direction @sort-state)}]]]
               [:tbody
                (doall
                 (for [{:keys [:attribute :value :actor-name :update-source :timestamp :info]}
                       sorted-updates]
                   (let [update-attribute attribute
                         new-value value
                         actor-display-name (actor-with-source actor-name update-source)
                         field-schema (get entity-schema update-attribute)
                         label (or (-> field-schema
                                       :field/translations
                                       :en)
                                   (loc/tr field-schema :field/attribute)
                                   (when (= attribute :common/ord)
                                     (loc/tr :move)))
                         grid-editor-type (-> field-schema
                                              :field/default-visibility
                                              :grid)
                         editor-type (cond (= attribute :common/related-entities)                   ;; TODO: Ugly hack - please fix asap
                                           :editor.type/select
                                           (= attribute :common/attachments)
                                           :editor.type/select
                                           :else
                                           grid-editor-type)
                         new-value (or (:db/id new-value) new-value)
                         str-value (get-display-string value field-schema editor-type new-value)
                         value-col-hiccup (get-value-hiccup {:update-attribute update-attribute
                                                             :new-value        new-value
                                                             :str-value        str-value
                                                             :info             info
                                                             :timestamp        timestamp
                                                             :value            value
                                                             :entity-schema    entity-schema
                                                             :field-schema     field-schema})
                         short-update-text (cond
                                             (some #{(->> update-attribute
                                                          (get entity-schema)
                                                          :field/data-type)}
                                                   [:data.type/financials
                                                    :data.type/tesi-financials])
                                             (loc/tr :click-to-see-old-value)
                                             (some #{(->> update-attribute
                                                          (get entity-schema)
                                                          :field/data-type)}
                                                   [:data.type/text-edn])
                                             (loc/tr :click-to-see-old-value)
                                             :else
                                             value-col-hiccup)]
                     (if (cu/blank-value? (str label))
                       nil
                       [:tr.history-row
                        {:key      (str timestamp "-" attribute "->" value)
                         :on-click (fn [_]
                                     (show-details update-attribute
                                                   entity-schema
                                                   actor-display-name
                                                   timestamp
                                                   date-format
                                                   timezone
                                                   value-col-hiccup))}
                        [column-text
                         (when-let [fs (registry/get-field-schema tenant-schema
                                                                  update-attribute)]
                           (loc/field-name update-attribute fs))]
                        [column-hiccup short-update-text]
                        [column-text actor-display-name]
                        [column-text
                         (date-formats/format-millis
                          timestamp
                          date-format
                          timezone)]]))))]]]))])})))
